import React from 'react';

import type { ExtensionHandlers, ExtensionParams } from '@atlaskit/editor-common/extensions';

import { CARDS_EXTENSION_TYPE } from '@atlassian/cards-extension';
import type { CardsExtensionParameters } from '@atlassian/cards-extension';

import { LoadablePaint } from '@confluence/loadable';
import { fg } from '@confluence/feature-gating';

import { spotlightExtensionType } from '../spotlight/spotlightConstants';
import { LinkCardsLoader } from '../link-cards/view/LinkCardsLoader';
import type { SpotlightParameters } from '../spotlight/spotlightTypes';
import { carouselExtensionType } from '../carousel/carouselConstants';
import { CarouselLoader } from '../carousel/view/CarouselLoader';
import { hubElementPlaceholderExtensionType } from '../hub-element-placeholder/hubElementPlaceholderConstants';
import { linksMenuExtensionType } from '../links-menu/linksMenuExtensionType';
import { LinksMenuLoader } from '../links-menu/view/LinksMenuLoader';
import type { LinksMenuParameters } from '../links-menu/linksMenuTypes';
import { SpotlightLoader } from '../spotlight/view/SpotlightLoader';
import { hubSectionExtensionType } from '../section/sectionConstants';
import type { SearchExtensionParameters } from '../search/SearchExtension';
import { searchExtensionType } from '../search/searchConstants';
import { SearchExtensionLoader } from '../search/SearchExtensionLoader';
import { ConfluenceDependenciesProvider } from '../platform-props-providers/ConfluenceDependenciesProvider';
import type { CarouselParameters } from '../carousel/carouselTypes';

const PlatformSearchExtensionLoader = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PlatformSearchExtension" */ '@atlassian/custom-search-extension'
			)
		).CustomSearchExtension,
});

type getRendererCustomSitesExtensionHandlersProps = {
	contentId: string | undefined;
};

export const getRendererCustomSitesExtensionHandlers = ({
	contentId,
}: getRendererCustomSitesExtensionHandlersProps): ExtensionHandlers => {
	const extensionHandlers: ExtensionHandlers = {
		[CARDS_EXTENSION_TYPE]: () => <></>,
		[carouselExtensionType]: () => <></>,
		[linksMenuExtensionType]: () => <></>,
		[hubElementPlaceholderExtensionType]: () => <></>,
		[spotlightExtensionType]: () => <></>,
		[hubSectionExtensionType]: () => <></>,
		[searchExtensionType]: () => <></>,
	};

	if (contentId) {
		extensionHandlers[CARDS_EXTENSION_TYPE] = (
			extension: ExtensionParams<CardsExtensionParameters>,
		) => <LinkCardsLoader extensionNode={extension} contentId={contentId} />;
		extensionHandlers[carouselExtensionType] = (extension: ExtensionParams<CarouselParameters>) => (
			<CarouselLoader extensionNode={extension} contentId={contentId} />
		);
		extensionHandlers[linksMenuExtensionType] = (
			extension: ExtensionParams<LinksMenuParameters>,
		) => <LinksMenuLoader extensionNode={extension} contentId={contentId} />;
		extensionHandlers[spotlightExtensionType] = (
			extension: ExtensionParams<SpotlightParameters>,
		) => <SpotlightLoader extensionNode={extension} contentId={contentId} />;
		if (fg('company_hub_section_extension')) {
			extensionHandlers[hubElementPlaceholderExtensionType] = () => null;
		}
		if (fg('company_hub_search_extension')) {
			extensionHandlers[searchExtensionType] = (
				extension: ExtensionParams<SearchExtensionParameters>,
			) =>
				fg('company_hub_search_extension_platformization') ? (
					<PlatformSearchExtensionLoader
						mode="renderer"
						contentId={contentId}
						extensionNode={extension}
						propsProvider={ConfluenceDependenciesProvider}
					/>
				) : (
					<SearchExtensionLoader extensionNode={extension} contentId={contentId} mode="renderer" />
				);
		}
	}
	return extensionHandlers;
};
